/* eslint-disable @typescript-eslint/require-await */
import Joi from '@hapi/joi';
import classNames from 'classnames';
import { get } from 'lodash';
import React from 'react';
import { useNavigate } from 'react-router';
import { FeatureFlags } from '../../../../../common/src/FeatureFlags';
import { useTranslate } from '../../../mixins/translate/translate';
import { fetchDelete, fetchPost } from '../../../store/fetch';
import { AssetTypes as assetTypes } from '../../../utils/enums/AssetTypes';
import { SurveyEditDisplayTypes } from '../../../utils/enums/SurveyEditDisplayTypes';
import { hasFeatureFlag } from '../../../utils/hasFeatureFlag';
import { usePatchedDropdownMenu } from '../../../utils/usePatchedDropdownMenu';
import { Modal } from '../../common/Modal/Modal';
import { EditPanelButton, EditPanelButtons, EditPanelCell, EditPanelInput, EditPanelRow, EditPanelSection } from '../../elements';
import { ElementSelection } from '../../Projects/ElementSelection';
import { showAssetInNewWindow } from '../../utils/showAssetInNewWindow';
import { IAsset } from '../Interfaces/IAsset';

// eslint-disable-next-line @typescript-eslint/naming-convention
type canAddAnnotations = { canAddAnnotations: true; returnUrl: string } | { canAddAnnotations?: undefined | false };

interface IAssetTemplateProps {
    type?: string | undefined;
    canAddToReport?: boolean | undefined;
    canAddToReportDocument?: boolean | undefined;
    canCopyAndMove?: boolean | undefined;
    canEditDescription?: boolean | undefined;
    canEditExternalUrl?: boolean | undefined;
    canRemove?: boolean | undefined;
    canSort?: boolean | undefined;
    canAddToStartPage?: boolean | undefined;
    date?: Date | undefined;
    description?: string | undefined | null;
    displayType?: SurveyEditDisplayTypes | undefined;
    externalUrl?: string | undefined;
    getImageUrl?: IAsset | undefined;
    id: string;
    isEemuaOrApi?: boolean | undefined;
    isInConditionReport?: boolean | undefined;
    isInMSWordReport?: boolean | undefined;
    isOnReportStartPage?: boolean | undefined;
    isShowDescription?: boolean | undefined;
    name?: string | undefined;
    objectId?: string | undefined;
    order?: number | undefined;
    preventAssetDelete?: boolean | undefined;
    size?: number | undefined;
    submitting?: boolean | undefined;
    surveyId?: string | undefined;
    equipmentId?: string | undefined;
    onAssetsSorted?: (result: any) => void;
    onAssetTextChange?: (description: string, assetId: string) => void;
    onShowAsset?: (e: React.MouseEvent<any>, asset: IAsset) => void;
    onAssetsChanged(): Promise<void>;
    onAssetRemoved?(): void;
}

const sumReducer = (s: number, a: number): number => s + a;

function getImageUrl(asset: IAsset, equipmentId?: string): string {
    if (asset.type === assetTypes.MeasurementXLS) {
        return '/assets/img/excel_thmb.png';
    } else if (asset.type === assetTypes.ZipArchive) {
        return '/assets/img/zip_thmb.png';
    } else if (asset.type === assetTypes.Document) {
        return '/assets/img/word_thmb.png';
    } else if (asset.type === assetTypes['3DModel']) {
        return '/assets/img/3d_thmb.png';
    } else if (asset.type === assetTypes.MeasurementPDF) {
        if (asset.filenameThumbnail) {
            if (equipmentId) {
                return `/api/equipmentAsset/${asset.id}/download?thumb=true`;
            }
            return `/api/assets/${asset.id}/download?thumb=true`;
        }
        return '/assets/img/pdf_thmb.png';
    } else if (asset.type === assetTypes.Image || asset.type === assetTypes.Image360 || asset.type === assetTypes.ImageTif) {
        if (asset.filenameThumbnail) {
            if (equipmentId) {
                return `/api/equipmentAsset/${asset.id}/download?thumb=true`;
            }
            return `/api/assets/${asset.id}/download?thumb=true`;
        }
        return '/assets/img/image_thmb.png';
    } else if (equipmentId) {
        return `/api/equipmentAsset/${asset.id}/download?thumb=true`;
    }
    return `/api/assets/${asset.id}/download?thumb=true`;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export function AssetTemplate(props: IAssetTemplateProps & canAddAnnotations) {
    const navigate = useNavigate();
    const translate = useTranslate();
    const [showCopyOrMove, setShowCopyOrMove] = React.useState<'none' | 'copy' | 'move'>('none');
    const [isShowExternalUrl, setShowExternalUrl] = React.useState(false);
    const [isShowConfirmRemove, setShownConfirmRemove] = React.useState(false);
    const [externalUrl, setExternalUrl] = React.useState(props.externalUrl ?? undefined);
    const [elementSelectionState, setElementSelectionState] = React.useState<{
        elementId?: undefined | string | null;
        unitId?: undefined | string | null;
        manifestationId?: undefined | string | null;
    }>({});

    async function saveAssetData(isAssetInOnlineReport: boolean, isAssetInReportDocument: boolean, isAssetOnStartPage: boolean) {
        const data = {
            isInConditionReport: isAssetInOnlineReport,
            isInMSWordReport: isAssetInReportDocument,
            isOnReportStartPage: isAssetOnStartPage,
        };
        const url = props.surveyId ? `/api/surveys/${props.surveyId}/assets/${props.id}` : `/api/objects/${props.objectId}/assets/${props.id}`;
        await fetchPost(url, undefined, data);
        await props.onAssetsChanged();
    }

    const menuPresences = [
        Number(Boolean(props.type === assetTypes.Image360)),
        Number(Boolean(hasFeatureFlag(FeatureFlags.Image360) && props.type === assetTypes.Image && !props.equipmentId)),
        props.canCopyAndMove ? 2 : 0,
        Number(Boolean(props.canRemove)),
        Number(Boolean(props.canEditExternalUrl)),
        Number(
            Boolean(
                props.canAddAnnotations &&
                (props.type === assetTypes.ImageAnnotation || props.type === assetTypes.Image || props.type === assetTypes.DerivedImage),
            ),
        ),
        Number(
            Boolean(
                (props.type === assetTypes.ImageAnnotation || props.type === assetTypes.Image || props.type === assetTypes.DerivedImage) &&
                !props.equipmentId,
            ),
        ),
        Number(Boolean(props.canSort && props.order !== 0)),
        Number(Boolean(props.canSort)),
        Number(Boolean(props.canSort && props.order !== 0)),
    ];

    const amountOfMenuItems = menuPresences.reduce(sumReducer, 0);
    const { buttonProps, itemProps, isOpen, setIsOpen } = usePatchedDropdownMenu(amountOfMenuItems);
    const classes = classNames('btn-group mt5', { open: isOpen });

    const getItemOptions = (index: number): number => {
        const resultedIndex = menuPresences.slice(0, index + 1).reduce(sumReducer, 0);
        // indexes start from 0
        return resultedIndex - 1;
    };

    async function remove360() {
        setIsOpen(false);
        const url = props.surveyId ? `/api/surveys/${props.surveyId}/assets/${props.id}` : `/api/objects/${props.objectId}/assets/${props.id}`;
        await fetchPost(url, undefined, { type: assetTypes.Image });
        await props.onAssetsChanged();
    }

    async function set360() {
        setIsOpen(false);
        const url = props.surveyId ? `/api/surveys/${props.surveyId}/assets/${props.id}` : `/api/objects/${props.objectId}/assets/${props.id}`;
        await fetchPost(url, undefined, { type: assetTypes.Image360 });
        await props.onAssetsChanged();
    }

    async function saveAssetDescription(asset: IAsset) {
        const data = {
            description: asset.description,
        };
        const url = props.surveyId ? `/api/surveys/${props.surveyId}/assets/${props.id}` : `/api/objects/${props.objectId}/assets/${props.id}`;
        await fetchPost(url, undefined, data);
        await props.onAssetsChanged();
    }

    async function saveExternalUr() {
        await fetchPost(`/api/surveys/${props.surveyId}/assets/${props.id}`, undefined, { externalUrl });
        setShowExternalUrl(false);
        await props.onAssetsChanged();
    }

    async function removeAsset() {
        if (!props.preventAssetDelete) {
            const url = props.surveyId ? `/api/surveys/${props.surveyId}/assets/${props.id}` : `/api/objects/${props.objectId}/assets/${props.id}`;
            await fetchDelete(url);
        }
        await props.onAssetsChanged();
        if (props.onAssetRemoved) {
            props.onAssetRemoved();
        }
        setShownConfirmRemove(false);
    }

    async function moveAsset() {
        setShowCopyOrMove('none');
        await fetchPost(`/api/surveys/${props.surveyId}/assets/${props.id}/move`, undefined, elementSelectionState);
        setElementSelectionState({});
        await props.onAssetsChanged();
    }

    async function copyAsset() {
        setShowCopyOrMove('none');
        await fetchPost(`/api/surveys/${props.surveyId}/assets/${props.id}/copy`, undefined, elementSelectionState);
        setElementSelectionState({});
        await props.onAssetsChanged();
    }

    function showAsset(e: React.MouseEvent<any>, asset: IAsset, equipmentId?: string) {
        if (props.onShowAsset) {
            props.onShowAsset(e, asset);
        } else {
            e.preventDefault();
            showAssetInNewWindow(asset, { equipmentId });
        }
    }
    function validUrl(url?: string) {
        if (url) {
            if (url.length > 254) {
                return false;
            }
            const validationResults = Joi.validate(url, Joi.string().uri());
            return !validationResults.error;
        }
        return true;
    }

    function openInWindow(url: string) {
        window.open(url, undefined, 'height=600,width=960,status=yes,resizable=yes,toolbar=no,menubar=no,location=no');
    }

    const redirectToAnnotations = () => {
        setIsOpen(false);
        // NOTE props.returnUrl is not present in interface
        // TODO need to take a look at that...
        const returnUrlValue = get(props, 'returnUrl') ?? '';
        navigate(`/annotater/${props.id}?surveyid=${props.surveyId}&objectid=${props.objectId}&returnUrl=${encodeURIComponent(returnUrlValue)}`);
    };

    const assetAttionRemovalHandler = async () => {
        setIsOpen(false);
        await saveAssetData(props.isInConditionReport!, props.isInMSWordReport!, !props.isOnReportStartPage);
    };

    return (
        <div>
            <div className="thumbnail" style={{ marginLeft: '-5px', marginRight: '-5px' }}>
                {props.canSort && (
                    <span className="column-drag-handle" style={{ float: 'left', cursor: 'move', color: '#000' }}>
                        &#x2630;
                    </span>
                )}
                <a href="#" onClick={(e) => showAsset(e, props as any as IAsset, props.equipmentId)}>
                    <img
                        loading="lazy"
                        src={getImageUrl(props as any as IAsset, props.equipmentId)}
                        className={'img-responsive'}
                        title={props.name}
                    />

                    <div className="status_icon_image">
                        <span className="fa fa-drum-steelpan p5" title="Video"></span>
                        {props.type === assetTypes.DerivedVideo && <span className="fa fa-film p5" title="Video"></span>}
                        {props.type === assetTypes.Image360 && <span className="fa fa-cube p5" title="Photo"></span>}
                        {props.externalUrl && props.externalUrl !== '' && <span className="fa fa-external-link p5" title="Link"></span>}
                        {(props.type === assetTypes.DerivedImage || props.type === assetTypes.Image) && (
                            <span className="fa fa-picture-o p5" title="Photo"></span>
                        )}
                        {(props.type === assetTypes.MeasurementPDF || props.type === assetTypes.MeasurementXLS) && (
                            <span className="fa fa-download p5" title="Video"></span>
                        )}
                        {props.isInConditionReport && <span className="fa fa-globe p5" title={translate('survey.imageUsedInReport')}></span>}
                        {props.isInMSWordReport && <span className="fa fa-file-text p5" title={translate('survey.imageUsedInReport')}></span>}
                        {props.isOnReportStartPage && <span className="fa fa-bookmark-o p5" title={translate('survey.imageOnStartPage')}></span>}
                        {props.type === assetTypes.ImageAnnotation && <span className="fa fa-tag p5" title="Annotations"></span>}
                    </div>
                </a>
                <div className="caption pn">
                    <div className="text-right mt5">
                        {props.canEditDescription && (
                            <textarea
                                className="form-control fs12"
                                id="comment"
                                rows={3}
                                name="comment"
                                onChange={(e) => props.onAssetTextChange!(e.target.value, props.id)}
                                onBlur={async () => saveAssetDescription(props as any as IAsset)}
                                value={props.description ?? ''}
                            />
                        )}
                        {!props.canEditDescription && props.isShowDescription && (
                            <div
                                className="text-left fw200 fs12 p5"
                                style={{ minHeight: '80px', maxHeight: '80px', overflow: 'hidden', overflowX: 'hidden' }}
                            >
                                {' '}
                                {props.description}
                            </div>
                        )}

                        <div style={{ float: 'left' }}>
                            {props.canAddToReport && (
                                <button
                                    type="button"
                                    className={
                                        props.isInConditionReport
                                            ? 'btn btn-sm btn-success mt5 fs12 white-text'
                                            : 'btn btn-sm btn-default fw600  mt5 fs12'
                                    }
                                    title={
                                        props.isInConditionReport ? translate('survey.assetRemoveFromReport') : translate('survey.assetAddToReport')
                                    }
                                    onClick={async () =>
                                        saveAssetData(!props.isInConditionReport, props.isInMSWordReport!, props.isOnReportStartPage!)
                                    }
                                >
                                    <span className="fa fa-globe"></span>
                                </button>
                            )}

                            {props.canAddToReportDocument && props.type !== assetTypes.MeasurementPDF && props.type !== assetTypes.MeasurementXLS && (
                                <button
                                    type="button"
                                    className={
                                        props.isInMSWordReport
                                            ? 'btn btn-sm btn-success fw600 mt5 ml5 fs12 bg-light white-text'
                                            : 'btn btn-sm btn-default fw600  mt5 ml5 fs12 bg-light'
                                    }
                                    title={
                                        props.isInMSWordReport
                                            ? translate('survey.assetRemoveFromMSWReport')
                                            : translate('survey.assetAddToMSWReport')
                                    }
                                    onClick={async () =>
                                        saveAssetData(props.isInConditionReport!, !props.isInMSWordReport, props.isOnReportStartPage!)
                                    }
                                >
                                    <span className="fa fa-file-text"></span>
                                </button>
                            )}
                        </div>

                        {(props.canRemove || props.canAddAnnotations || props.canCopyAndMove || props.canSort) && (
                            <div className={classes}>
                                <button
                                    {...buttonProps}
                                    className="btn btn-default btn-sm fs12 fw500 dropdown-toggle ph10"
                                    data-toggle="dropdown"
                                    disabled={props.submitting}
                                    type="button"
                                >
                                    {translate('common.actions')}
                                    <span className="caret ml5"></span>
                                </button>
                                <ul className="dropdown-menu pull-right list-group animated animated-short" role="menu">
                                    {props.type === assetTypes.Image360 && (
                                        <li key="360" {...itemProps[getItemOptions(0)]} onClick={remove360}>
                                            <a className="cursor">
                                                <span className="fa fa-cube mr10" />
                                                {translate('asset.remove360')}
                                            </a>
                                        </li>
                                    )}
                                    {hasFeatureFlag(FeatureFlags.Image360) && props.type === assetTypes.Image && !props.equipmentId && (
                                        <li key="360" {...itemProps[getItemOptions(1)]} onClick={set360}>
                                            <a className="cursor">
                                                <span className="fa fa-cube mr10" />
                                                {translate('asset.set360')}
                                            </a>
                                        </li>
                                    )}
                                    {props.canCopyAndMove && (
                                        <>
                                            <li
                                                key="copy"
                                                {...itemProps[getItemOptions(2) - 1]}
                                                onClick={() => {
                                                    setShowCopyOrMove('copy');
                                                    setIsOpen(false);
                                                }}
                                            >
                                                <a className="cursor">
                                                    <span className="fa fa-copy mr10" />
                                                    {translate('common.copy')}
                                                </a>
                                            </li>
                                            <li
                                                key="move"
                                                {...itemProps[getItemOptions(2)]}
                                                onClick={() => {
                                                    setShowCopyOrMove('move');
                                                    setIsOpen(false);
                                                }}
                                            >
                                                <a className="cursor">
                                                    <span className="fa fa-cut mr10" />
                                                    {translate('common.move')}
                                                </a>
                                            </li>
                                        </>
                                    )}
                                    {props.canRemove && (
                                        <li
                                            {...itemProps[getItemOptions(3)]}
                                            onClick={() => {
                                                setIsOpen(false);
                                                setShownConfirmRemove(true);
                                            }}
                                        >
                                            <a className="cursor">
                                                <span className="fa fa-trash-o mr10" />
                                                {translate('common.remove')}
                                            </a>
                                        </li>
                                    )}
                                    {props.canEditExternalUrl && (
                                        <li
                                            {...itemProps[getItemOptions(4)]}
                                            onClick={() => {
                                                setIsOpen(false);
                                                setShowExternalUrl(true);
                                            }}
                                        >
                                            <a className="cursor">
                                                <span className="fa fa-external-link mr10" />
                                                {translate('survey.externalLink')}
                                            </a>
                                        </li>
                                    )}
                                    {props.canAddAnnotations &&
                                        (props.type === assetTypes.ImageAnnotation ||
                                            props.type === assetTypes.Image ||
                                            props.type === assetTypes.DerivedImage) && (
                                            <li {...itemProps[getItemOptions(5)]} onClick={redirectToAnnotations}>
                                                <a className="cursor">
                                                    <span className="fa fa-tag mr10" />
                                                    {translate('common.annotations')}
                                                </a>
                                            </li>
                                        )}
                                    {props.canAddToStartPage &&
                                        (props.type === assetTypes.ImageAnnotation ||
                                            props.type === assetTypes.Image ||
                                            props.type === assetTypes.DerivedImage) &&
                                        !props.equipmentId && (
                                            <li {...itemProps[getItemOptions(6)]} onClick={assetAttionRemovalHandler}>
                                                <a className="cursor">
                                                    <span className="fa fa-bookmark-o mr10" />
                                                    {props.isOnReportStartPage
                                                        ? translate('survey.assetRemoveFromStartPage')
                                                        : translate('survey.assetAddToStartPage')}
                                                </a>
                                            </li>
                                        )}
                                    {props.canSort && (
                                        <>
                                            <li className="divider"></li>
                                            {props.order !== 0 && (
                                                <li
                                                    key="up"
                                                    {...itemProps[getItemOptions(7)]}
                                                    onClick={() => {
                                                        setIsOpen(false);
                                                        props.onAssetsSorted!({ removedIndex: props.order, addedIndex: props.order! - 1 });
                                                    }}
                                                >
                                                    <a className="cursor">
                                                        <span className="fa fa-angle-up mr10" />
                                                        {translate('common.up')}
                                                    </a>
                                                </li>
                                            )}
                                            <li
                                                key="down"
                                                {...itemProps[getItemOptions(8)]}
                                                onClick={() => {
                                                    setIsOpen(false);
                                                    props.onAssetsSorted!({ removedIndex: props.order, addedIndex: props.order! + 1 });
                                                }}
                                            >
                                                <a className="cursor">
                                                    <span className="fa fa-angle-down mr10" />
                                                    {translate('common.down')}
                                                </a>
                                            </li>
                                            {props.order !== 0 && (
                                                <li
                                                    key="top"
                                                    {...itemProps[getItemOptions(9)]}
                                                    onClick={() => {
                                                        setIsOpen(false);
                                                        props.onAssetsSorted!({ removedIndex: props.order, addedIndex: 0 });
                                                    }}
                                                >
                                                    <a className="cursor">
                                                        <span className="fa fa-angle-double-up mr10" />
                                                        {translate('common.top')}
                                                    </a>
                                                </li>
                                            )}
                                        </>
                                    )}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {showCopyOrMove !== 'none' && (
                <>
                    <Modal id="copyOrMove" onClose={() => setShowCopyOrMove('none')} closeOnBackdropClick={true}>
                        <Modal.Title shouldHideCloseButton={true}>
                            {showCopyOrMove === 'move' && translate('survey.moveImageTo')}
                            {showCopyOrMove === 'copy' && translate('survey.copyImageTo')}
                        </Modal.Title>
                        <Modal.Content isScrollBarVisible={false} style={{ height: '150px' }}>
                            <EditPanelSection>
                                <ElementSelection
                                    surveyId={props.surveyId as string}
                                    onChange={async (value) => setElementSelectionState(value)}
                                    elementId={elementSelectionState.elementId}
                                    unitId={elementSelectionState.unitId}
                                    manifestationId={elementSelectionState.manifestationId}
                                />
                            </EditPanelSection>
                        </Modal.Content>
                        <Modal.Actions>
                            <EditPanelButtons fixWidth={true}>
                                <EditPanelButton
                                    onClick={() => {
                                        setShowCopyOrMove('none');
                                        setElementSelectionState({});
                                    }}
                                    small={true}
                                    text={translate('common.cancel')}
                                />
                                {showCopyOrMove === 'move' && (
                                    <EditPanelButton onClick={async () => moveAsset()} small={true} primary={true} text={translate('common.move')} />
                                )}
                                {showCopyOrMove === 'copy' && (
                                    <EditPanelButton onClick={async () => copyAsset()} small={true} primary={true} text={translate('common.copy')} />
                                )}
                            </EditPanelButtons>
                        </Modal.Actions>
                    </Modal>
                </>
            )}
            {isShowExternalUrl && (
                <>
                    <Modal id="externalUrl" onClose={() => setShowExternalUrl(false)} closeOnBackdropClick={true}>
                        <Modal.Title shouldHideCloseButton={true}>{translate('survey.externalLink')}</Modal.Title>
                        <Modal.Content isScrollBarVisible={false} isForm={true} style={{ height: '50px' }}>
                            <EditPanelRow fixWidth={false}>
                                <EditPanelCell rowCount={12}>
                                    <EditPanelInput
                                        name="externalLink"
                                        placeholder={translate('survey.externalLink')}
                                        value={externalUrl}
                                        notValid={!validUrl(externalUrl)}
                                        handleChange={async (value) => setExternalUrl(value)}
                                        handleEnter={async () => saveExternalUr()}
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                        </Modal.Content>
                        <Modal.Actions>
                            <EditPanelButtons fixWidth={true}>
                                <EditPanelButton small={true} text={translate('common.cancel')} onClick={() => setShowExternalUrl(false)} />
                                {externalUrl && <EditPanelButton onClick={() => openInWindow(externalUrl)} small={true} text="Open link" />}
                                <EditPanelButton onClick={async () => saveExternalUr()} small={true} primary={true} text={translate('common.save')} />
                            </EditPanelButtons>
                        </Modal.Actions>
                    </Modal>
                </>
            )}
            {isShowConfirmRemove && (
                <Modal id="confirm-dialog" classNames="confirm-dialog" closeOnBackdropClick={true} onClose={() => setShownConfirmRemove(false)}>
                    <Modal.Content isScrollBarVisible={false} style={{ height: '50px' }}>
                        <strong className="mt10">{translate('common.confirmDeleteItem')}</strong>
                    </Modal.Content>
                    <Modal.Actions>
                        <button className="btn btn-sm btn-default" onClick={() => setShownConfirmRemove(false)}>
                            {translate('common.cancel')}
                        </button>
                        <button className="btn btn-sm btn-danger" onClick={async () => removeAsset()} autoFocus>
                            {translate('common.remove')}
                        </button>
                    </Modal.Actions>
                </Modal>
            )}
        </div>
    );
}
